import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SimpleGrid, SlideFade } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getInseeCodeFromLegalForm,
  LEGAL_FORMS,
} from '@shinetools/legal-form-library';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import { formatAmount } from 'common/formatAmount';
import Accordion from 'components/_core/Accordion';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import CapitalDepositQuestionPage from '../../components/QuestionPage';
import {
  CapitalInfoSchema,
  getCapitalInfoSchema,
} from '../../libs/capitalInfoSchema';
import { CapitalDepositPage } from '../../types';
import locales from './locales';

const CAPITAL_PERCENTAGE_DEFAULT_FORM_VALUES = [100, 50, 20];
const CAPITAL_PERCENTAGE_DEFAULT_FORM_VALUES_SAS = [100, 50];

export interface CapitalInfoData {
  legalNumberOfShares: number;
  legalShareCapital: number;
  paidUpCapitalPercentage: number;
}

type CapitalDepositCompanyLegalInfoProps = {
  onSubmit: (values: CapitalInfoData) => void;
  formData: Partial<CapitalInfoData>;
  isSending: boolean;
  isMandatee: boolean;
  inseeLegalFormCode: string | null;
} & CapitalDepositPage;

const CapitalDepositCompanyLegalInfo =
  asBentoPage<CapitalDepositCompanyLegalInfoProps>(
    ({
      formData,
      inseeLegalFormCode,
      isMandatee,
      isSending,
      Layout,
      onPrev,
      onSubmit,
      progress,
    }) => {
      const schema = useMemo(
        () => getCapitalInfoSchema(inseeLegalFormCode),
        [inseeLegalFormCode],
      );

      const initialPaidUpCapitalPercentage = formData.paidUpCapitalPercentage;

      const getPaidUpCapitalPercentageDefaultInitialValue = () => {
        if (initialPaidUpCapitalPercentage === undefined) {
          return undefined;
        }

        return CAPITAL_PERCENTAGE_DEFAULT_FORM_VALUES.includes(
          initialPaidUpCapitalPercentage,
        )
          ? initialPaidUpCapitalPercentage
          : 'other';
      };

      const initialFormValues = {
        ...formData,
        paidUpCapitalPercentage:
          initialPaidUpCapitalPercentage &&
          CAPITAL_PERCENTAGE_DEFAULT_FORM_VALUES.includes(
            initialPaidUpCapitalPercentage,
          )
            ? undefined
            : initialPaidUpCapitalPercentage,
        paidUpCapitalPercentageDefault:
          getPaidUpCapitalPercentageDefaultInitialValue(),
      };

      const {
        control,
        formState: { errors },
        handleSubmit,
        register,
        setValue,
        watch,
      } = useForm<CapitalInfoSchema>({
        defaultValues: initialFormValues,
        mode: 'onSubmit',
        resolver: zodResolver(schema),
      });
      const [
        legalShareCapitalValue,
        paidUpCapitalPercentageSelectedValue,
        paidUpCapitalPercentageInputValue,
      ] = watch([
        'legalShareCapital',
        'paidUpCapitalPercentageDefault',
        'paidUpCapitalPercentage',
      ]);

      const paidUpCapitalPercentageDefaultValues =
        inseeLegalFormCode === getInseeCodeFromLegalForm(LEGAL_FORMS.SAS)
          ? CAPITAL_PERCENTAGE_DEFAULT_FORM_VALUES_SAS
          : CAPITAL_PERCENTAGE_DEFAULT_FORM_VALUES;

      const paidUpCapitalPercentageOptions = [
        ...paidUpCapitalPercentageDefaultValues.map((percentage) => ({
          label: `${percentage} %`,
          value: percentage,
        })),
        {
          label: locales.paidUpCapitalPercentage.other as string,
          value: 'other',
        },
      ];

      const hasErrors = Object.keys(errors).length > 0;

      const hasChosenOtherCapitalPercentageOption =
        paidUpCapitalPercentageSelectedValue === 'other';

      const paidUpCapitalPercentage = hasChosenOtherCapitalPercentageOption
        ? (paidUpCapitalPercentageInputValue as number)
        : (paidUpCapitalPercentageSelectedValue as number);

      const paidUpCapital =
        legalShareCapitalValue !== null && paidUpCapitalPercentage !== null
          ? (legalShareCapitalValue * paidUpCapitalPercentage) / 100
          : null;

      const shouldShowPaidUpCapitalPercentageWarning =
        !hasErrors && !!paidUpCapital;

      const submitHandler = handleSubmit((values) => {
        // handleSubmit is called only if form validation is successful
        onSubmit({
          legalNumberOfShares: values.legalNumberOfShares,
          legalShareCapital: values.legalShareCapital,
          paidUpCapitalPercentage: hasChosenOtherCapitalPercentageOption
            ? (paidUpCapitalPercentageInputValue as number)
            : (paidUpCapitalPercentageSelectedValue as number),
        });
      });

      const helpAccordion = (
        <Accordion>
          <Accordion.Item title={locales.legalShareCapital.helpTitle}>
            <Accordion.Text>
              {locales.legalShareCapital.helpDescription}
            </Accordion.Text>
          </Accordion.Item>
          <Accordion.Item title={locales.legalNumberOfShares.helpTitle}>
            <Accordion.Text>
              {isMandatee
                ? locales.legalNumberOfShares.helpDescription.mandate
                : locales.legalNumberOfShares.helpDescription.nonMandate}
            </Accordion.Text>
          </Accordion.Item>
        </Accordion>
      );

      return (
        <Layout nav={<OBNavigation onPrev={onPrev} />}>
          <CapitalDepositQuestionPage
            as="form"
            button={
              <Button icon="arrow-right" isLoading={isSending} type="submit">
                {locales.ctaSubmit}
              </Button>
            }
            onSubmit={submitHandler}
            progress={progress}
          >
            <Header
              description={
                isMandatee
                  ? locales.description.mandate
                  : locales.description.nonMandate
              }
              title={
                isMandatee ? locales.title.mandate : locales.title.nonMandate
              }
            />

            <SimpleGrid columns={2} marginBottom="space-24" spacing="space-24">
              <Form.Field
                error={errors.legalShareCapital?.message}
                label={locales.legalShareCapital.label}
              >
                <Form.Input
                  {...register('legalShareCapital', {
                    valueAsNumber: true,
                  })}
                  placeholder={locales.legalShareCapital.placeholder}
                  type="number"
                />
              </Form.Field>

              <Form.Field
                error={errors.legalNumberOfShares?.message}
                label={locales.legalNumberOfShares.label}
              >
                <Form.Input
                  {...register('legalNumberOfShares', {
                    valueAsNumber: true,
                  })}
                  placeholder={locales.legalNumberOfShares.placeholder}
                  type="number"
                />
              </Form.Field>
            </SimpleGrid>

            <Form.Field
              error={errors.paidUpCapitalPercentageDefault?.message}
              hint={
                paidUpCapitalPercentageSelectedValue === 20
                  ? locales.paidUpCapitalPercentage.hintTwentyPercent
                  : ''
              }
              label={locales.paidUpCapitalPercentage.defaultLabel}
              marginBottom="space-24"
            >
              <Controller
                control={control}
                name="paidUpCapitalPercentageDefault"
                render={({ field }) => {
                  const selectedOption = paidUpCapitalPercentageOptions.find(
                    (option) => option.value === field.value,
                  );

                  return (
                    <Form.SunshineSelect
                      onChange={(option) => {
                        if (
                          option?.value &&
                          typeof option?.value === 'number'
                        ) {
                          setValue('paidUpCapitalPercentage', undefined, {
                            shouldValidate: true,
                          });
                        }

                        field.onChange(option?.value);
                      }}
                      options={paidUpCapitalPercentageOptions}
                      placeholder={locales.paidUpCapitalPercentage.placeholder}
                      value={selectedOption}
                    />
                  );
                }}
              />
            </Form.Field>

            {hasChosenOtherCapitalPercentageOption ? (
              <Form.Field
                error={errors.paidUpCapitalPercentage?.message}
                hint={locales.paidUpCapitalPercentage.hintPercentage}
                label={locales.paidUpCapitalPercentage.label}
                marginBottom="space-24"
              >
                <Form.Input
                  {...register('paidUpCapitalPercentage', {
                    valueAsNumber: true,
                  })}
                  placeholder={locales.paidUpCapitalPercentage.otherPlaceholder}
                  type="number"
                />
              </Form.Field>
            ) : null}

            {shouldShowPaidUpCapitalPercentageWarning ? (
              <>
                <Callout marginBottom="space-24">
                  <Callout.Content>
                    {locales.formatString(
                      locales.paidUpCapitalPercentage.warning,
                      {
                        paidUpCapital: formatAmount({
                          forcePlusSign: false,
                          value: paidUpCapital,
                        }),
                      },
                    )}
                  </Callout.Content>
                </Callout>
                <SlideFade
                  in={shouldShowPaidUpCapitalPercentageWarning}
                  offsetY={-50}
                  transition={{
                    enter: { duration: 0.3 },
                    exit: { duration: 0 },
                  }}
                >
                  {helpAccordion}
                </SlideFade>
              </>
            ) : (
              <>
                {hasChosenOtherCapitalPercentageOption ? (
                  <>
                    <SlideFade
                      in={hasChosenOtherCapitalPercentageOption}
                      offsetY={-50}
                      transition={{
                        enter: { duration: 0.3 },
                        exit: { duration: 0 },
                      }}
                    >
                      {helpAccordion}
                    </SlideFade>
                  </>
                ) : (
                  <>{helpAccordion}</>
                )}
              </>
            )}
          </CapitalDepositQuestionPage>
        </Layout>
      );
    },
  );

export default CapitalDepositCompanyLegalInfo;
